import {
  Box,
  Card,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import {
  Log,
  getHistoryTerminalList,
  getPendingTerminalFilterConditions,
} from "../../services/services";
import Label from "../Label";
import ListHead from "../table/ListHead";
import Scrollbar from "../table/Scrollbar";
import { applySortFilter } from "../../utils/tableFunctions";
import { StyledTableRow } from "../TableRow";
import CircularLoader from "../circularLoader";
import CommonDialog from "../CustomDialog";
import TerminalDetails from "../../pages/details/admin/TerminalDetails";
import HeaderHelpPage from "../HeaderHelpPage";
import FormSearch from "../FormSearch";
import { pageNumber, rowsPerPage as defaultRowsPerPage } from "../../utils/constants";

const TABLE_HEAD = [
  { id: "terminal_id", label: "Terminal ID" },
  { id: "merchantName", label: "Merchant" },
  { id: "business_entity", label: "Business Entity" },
  { id: "is_approved", label: "Approved" },
  { id: "action", label: "Action" },
];

const HistoryTerminal = () => {
  const [order, setOrder] = useState("asc");
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState("name");
  const [terminalList, setTerminalList] = useState({ results: [], count: 0 });
  const [loaded, setLoaded] = useState(false);
  const [itemId, setItemId] = useState(null);
  const [pagination, setPagination] = useState({
    page: pageNumber,
    page_size: defaultRowsPerPage,
  });

  // filter
  const [terminalFilter, setTerminalFilter] = useState({});
  const [fLoaded, setFLoaded] = useState(false);
  const [filter, setFilter] = useState({});
  const [inputValues, setInputValues] = useState({});

  const CustomTableCell = ({ align, field, id }) => {
    return (
      <TableCell
        align={align}
        onClick={() => {
          // window.open(`/3/app/terminal-details/${id}?action=true`, "_blank");
          setItemId(id);
        }}
      >
        {field}
      </TableCell>
    );
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = terminalList.results.map((n) => n.id);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const queryPage = (queryParams, params) => {
    setLoaded(false);
    getHistoryTerminalList({
      setResponse: renderData,
      setLoaded: setLoaded,
      filter: params || filter,
      queryParams: queryParams
    });
    setPagination(queryParams);
  };

  const handleChangePage = (event, newPage) => {
    queryPage({
      page: newPage + 1,
      page_size: pagination.page_size,
    });
  };

  const handleChangeRowsPerPage = (event) => {
    queryPage({
      page: 1,
      page_size: parseInt(event.target.value, 10),
    });
  };


  const filteredList = applySortFilter(
    terminalList.results || [],
    order,
    orderBy
  );

  const isItemNotFound = filteredList.length === 0;

  const renderData = data => {
    const newData = data.results.map(item => ({
      ...item,
      merchantName: item.merchant?.name,
      mode: item.mode?.mode,
      business_entity: item.merchant?.business_entity?.business_name
    }))
    setTerminalList({
      ...data,
      results: newData
    })
  }

  useEffect(() => {
    queryPage(pagination);
    getPendingTerminalFilterConditions("history", setTerminalFilter, setFLoaded);
  }, []);

  const onFilterSubmit = (data) => {
    const newParams = {
      terminal_id: data.terminal_id,
      merchant: data.merchant?.id,
      business_entity: data.business_entity?.id,
      payment_method: data.payment_method?.id,
      terminal_id_input: inputValues?.terminal_id?.trim() || null,
      merchant_input: inputValues?.merchant?.trim() || null,
      business_entity_input: inputValues?.business_entity?.trim() || null,
    };
    const newPagination = {
      ...pagination,
      page: 1,
    };

    Log.info("Click button [Apply Filter] - Search Terminal history list", newParams);

    setFilter(newParams);
    queryPage(newPagination, newParams);
  };

  const onFilterReset = () => {
    setInputValues({});
    setFilter({});
    queryPage({ ...pagination, page: 1 }, {});
  };

  const schema = [
    {
      type: "search-select",
      label: "Terminal ID",
      param: "terminal_id",
      options: terminalFilter?.terminal_id?.map((terminal_id) => terminal_id),
      inputValues,
      setInputValues,
    },
    {
      type: "search-select",
      label: "Merchant",
      param: "merchant",
      options: terminalFilter?.merchant?.map((merchant) => ({
        label: merchant.merchant__name,
        id: merchant.merchant__id,
      })),
      inputValues,
      setInputValues,
    },
    {
      type: "search-select",
      label: "Business Entity",
      param: "business_entity",
      options: terminalFilter?.business_entity?.map((entity) => ({
        label: entity.merchant__business_entity__business_name,
        id: entity.merchant__business_entity__id,
      })),
      inputValues,
      setInputValues,
    },
    {
      type: "search-select",
      label: "Method",
      param: "payment_method",
      options: terminalFilter?.payment_method?.map((method) => ({
        label: method.name,
        id: method.id,
      })),
    },
  ];

  return (
    <Box>
      <FormSearch
        schema={schema}
        loading={!fLoaded}
        onSearch={onFilterSubmit}
        onReset={onFilterReset}
      />
      {loaded ?
        <Card>
          <Scrollbar>
            <TableContainer>
              <Table>
                <ListHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  rowCount={terminalList.count}
                  numSelected={selected.length}
                  onRequestSort={handleRequestSort}
                  onSelectAllClick={handleSelectAllClick}
                  checkbox={false}
                />

                <TableBody>
                  {filteredList
                      .map((row) => {
                        const { id, terminal_id, merchantName, is_approved, action, business_entity } =
                          row;
                        const isItemSelected = selected.indexOf(id) !== -1;

                        return (
                          <StyledTableRow
                            hover
                            key={id}
                            aria-checked={isItemSelected}
                          >
                            <CustomTableCell
                              field={terminal_id}
                              id={id}
                            />
                            <CustomTableCell
                              field={
                                merchantName ?? ""
                              }
                              id={id}
                            />
                            <CustomTableCell
                              field={
                                business_entity ?? ""
                              }
                              id={id}
                            />
                            <CustomTableCell
                              field={
                                <Label
                                  variant="ghost"
                                  color={is_approved ? "success" : "error"}
                                >
                                  {is_approved ? "YES" : "NO"}
                                </Label>
                              }
                              id={id}
                            />
                            <CustomTableCell
                              field={action}
                              id={id}
                            />
                          </StyledTableRow>
                        );
                      })}
                </TableBody>
                {isItemNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                        <Paper>
                          <Typography
                            gutterBottom
                            align="center"
                            variant="subtitle1"
                          >
                            There is no terminal actions history at the moment.
                          </Typography>
                        </Paper>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>
          <TablePagination
            rowsPerPageOptions={[25, 50, 100]}
            component="div"
            count={terminalList.count}
            rowsPerPage={pagination.page_size}
            page={pagination.page - 1}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>
        :
        <CircularLoader />
      }

      {itemId && (
        <CommonDialog
          title={<HeaderHelpPage title="Terminal Details" variant="h5" mb={0} />}
          onClose={() => setItemId(null)}
        >
          <TerminalDetails itemId={itemId} isViewHistory />
        </CommonDialog>
      )}
    </Box>
  );
};

export default HistoryTerminal;
